import type { TitleColumn } from '@/components/Table/types';
import type { StatisticsSeries } from '@/types/series';
import type { Aggregate, AggregateDelta, ColInfo, SeriesWithDelta, StatisticsData } from '@/types/statisticsApi';
import type { Compare, StatisticsResource } from '../types';

const UNKNOWN_NAME: Partial<Record<StatisticsResource, string>> = {
  agents: 'Uppgift saknas',
  agencies: 'Uppgift saknas',
};

export type StatisticsTableData<Series extends StatisticsSeries, C extends Compare> = TitleColumn & {
  [k in C extends null | undefined ? Series : SeriesWithDelta<Series>]: number | null;
};

export type FormattedTable<Series extends StatisticsSeries, C extends Compare> = {
  periods: StatisticsData<Series, C>['periods'];
  rows: StatisticsTableData<Series, C>[];
};

export const formatToTable = <Series extends StatisticsSeries, C extends Compare>(
  data: StatisticsData<Series, C>,
  resource?: StatisticsResource,
): FormattedTable<Series, C> => {
  const unknownName = resource ? UNKNOWN_NAME[resource] : undefined;

  const { delta, ...rest } = { ...data.aggregate } as AggregateDelta<Series> & Aggregate<Series>;

  const aggregate = Object.fromEntries(
    (Object.entries(rest) as [Series, (number | null)[]][]).map(([key, values]) => [key, values[0]]),
  );
  const aggregateDelta = Object.fromEntries(
    (Object.entries(delta ?? {}) as [Series, (number | null)[]][]).map(([key, values]) => [`${key}:delta`, values[0]]),
  );
  const tableSummary = {
    title: [{ name: 'Summering', id: -1 }],
    ...aggregate,
    ...aggregateDelta,
  };

  const tableValues = data.rows.map(({ name, id, cols }) => ({
    title: [!name && unknownName ? { name: unknownName, id, disableLink: true } : { name, id }],
    ...Object.fromEntries((Object.entries(cols) as [Series, ColInfo][]).map(([key, value]) => [key, value.values[0]])),
  }));

  return {
    periods: data.periods,
    rows: [tableSummary, ...tableValues] as StatisticsTableData<Series, C>[],
  };
};
